import { generateParameterSystemName } from '@brighterion/utils/parameters';
import { Formik, FormikProps } from 'formik';
import { FormikInputField } from 'Components/FormikComponents';
import React from 'react';
import useIsLeavingForm from 'Hooks/useIsLeavingForm';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'Components/Breadcrumbs/routes';
import { useHistory } from 'react-router-dom';
import { TFunction } from 'i18next';
import { CREATE_VELOCITY_PARAM_INPUTS } from 'Common/parameterEnums';
import { HandleBlur, HandleChange } from 'Components/FormikComponents/types';
import Header from 'Routes/Parameters/Pages/CreateVelocity/Header';
import InfoOverview from 'Routes/Parameters/components/VelocityInfoOverview';
import VelocityParamStatement from 'Routes/Parameters/components/VelocityParamStatement';
import Layout from 'Components/Layout';
import useAddVelocityParameter from 'Hooks/query-hooks/parameters/useAddVelocityParameter';
import { PARAMETERS_PARAM_TYPES } from 'Common/enums';
import { addVelocityValidationSchema } from './CreateVelocitySchema';
import styles from './CreateVelocityForm.css';

type CreateVelocityFormProps = {
    initialValues: ParameterVelocityFormSchema;
    disableInputField: boolean;
    velocityConfigData: VelocityConfig;
    parameterData: UseGetParametersResponse;
};

export const genParameterInfoOverviewTop = (
    t: TFunction,
    isSubmitting: boolean,
    values: ParameterVelocityFormSchema,
    handleChange: HandleChange,
    handleBlur: HandleBlur
) => {
    const generatedParameterSystemName = generateParameterSystemName(values.displayName);

    return [
        {
            id: CREATE_VELOCITY_PARAM_INPUTS.DISPLAY_NAME,
            label: t('Display Name'),
            data: (
                <FormikInputField
                    label={t('Display Name')}
                    name={CREATE_VELOCITY_PARAM_INPUTS.DISPLAY_NAME}
                    className={styles.nameField}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    disabled={isSubmitting}
                    optionalMsg={t('addValidParameterDisplayName')}
                />
            )
        },
        {
            id: CREATE_VELOCITY_PARAM_INPUTS.SYSTEM_NAME,
            label: t('System Name'),
            data: (
                <FormikInputField
                    label={t('System Name')}
                    name={CREATE_VELOCITY_PARAM_INPUTS.SYSTEM_NAME}
                    className={styles.nameField}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    // overrideValue is being used to display formatted display name in the screen
                    // it does not set the field value
                    overrideValue={generatedParameterSystemName}
                    disabled
                    optionalMsg={t('parameterSystemName')}
                />
            )
        }
    ];
};

export const genParameterInfoOverviewBottom = (
    t: TFunction,
    isSubmitting: boolean,
    // values: ParameterVelocityFormSchema,
    handleChange: HandleChange,
    handleBlur: HandleBlur
) => [
    {
        id: CREATE_VELOCITY_PARAM_INPUTS.DESCRIPTION,
        label: t('Parameter Description'),
        data: (
            <FormikInputField
                label={t('Parameter Description')}
                name={CREATE_VELOCITY_PARAM_INPUTS.DESCRIPTION}
                className={styles.descriptionField}
                handleChange={handleChange}
                handleBlur={handleBlur}
                disabled={isSubmitting}
            />
        )
    }
];

const CreateVelocityForm: React.FC<CreateVelocityFormProps> = ({
    initialValues,
    disableInputField,
    parameterData,
    velocityConfigData
}) => {
    const formRef = React.useRef<FormikProps<ParameterVelocityFormSchema>>(null);
    useIsLeavingForm(formRef);
    const { t } = useTranslation();
    const { push } = useHistory();
    const mutation = useAddVelocityParameter(formRef);

    const handleFormSubmit = (formValues: ParameterVelocityFormSchema) => {
        const formattedSystemName = generateParameterSystemName(formValues.displayName);
        const {
            displayName,
            description,
            type,
            velocityType,
            includeCurrentEvent,
            aggregationField,
            timePeriod,
            indexes,
            filters
        } = formValues;

        // eslint-disable-next-line
        const payload: CreateVelocityParameterRequest = {
            fieldName: formattedSystemName,
            message: displayName,
            description,
            type: type || PARAMETERS_PARAM_TYPES.NUMBER,
            status: 'draft', // Hardcoded to draft until the deploy velocity feature is finished
            includeCurrentEvent,
            velocityType,
            timePeriod: {
                duration: timePeriod.duration,
                type: 'Value',
                unit: timePeriod.unit.toString()
            },
            indexes
        };

        // Conditionally add filters to the payload
        if (filters.length > 0) {
            payload.filters = filters;
        }

        // Conditionally add aggregationField to the payload
        if (aggregationField && aggregationField.length > 0) {
            payload.aggregationField = aggregationField.toString();
        }

        mutation.mutate(payload);
    };

    const handleCancel = () => {
        push(ROUTES.PARAMETERS.INDEX);
    };
    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
            validationSchema={addVelocityValidationSchema(t)}
            innerRef={formRef}
        >
            {(formikProps) => {
                const { values, handleChange, handleBlur, isSubmitting } = formikProps;

                const paramInfoOverviewTopData = genParameterInfoOverviewTop(
                    t,
                    isSubmitting,
                    values,
                    handleChange,
                    handleBlur
                );
                const paramInfoOverviewBottomData = genParameterInfoOverviewBottom(
                    t,
                    isSubmitting,
                    handleChange,
                    handleBlur
                );

                return (
                    <Layout>
                        <Header handleCancel={handleCancel} mode='create' />
                        <InfoOverview
                            dataTestId='velocityParameterInformation'
                            leftTitle={t('Parameter Information')}
                            topData={paramInfoOverviewTopData}
                            bottomData={paramInfoOverviewBottomData}
                            isSubmitting={isSubmitting}
                            values={values}
                        />
                        <VelocityParamStatement
                            disableInputField={disableInputField}
                            parameterData={parameterData}
                            velocityConfigData={velocityConfigData}
                        />
                    </Layout>
                );
            }}
        </Formik>
    );
};

export default CreateVelocityForm;
